import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useStoresQuery } from "../../Services/store_service";
import type { Store } from "../../Types/Product";

interface ReportStoreContextValue {
  stores: Store[];
  isLoadingStores: boolean;
  selectedStoreIds: string[];
  setSelectedStoreIds: (ids: string[]) => void;
  storeFilterKey: string;
}

const ReportStoreContext = createContext<ReportStoreContextValue | null>(null);

export const ReportStoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);

  const storesQuery = useStoresQuery(
    { page: 1, limit: 200 },
    { enabled: true },
  );

  const stores = useMemo<Store[]>(() => {
    const data = storesQuery.data;
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray((data as any).stores)) {
      return (data as any).stores as Store[];
    }
    return [];
  }, [storesQuery.data]);

  // Store filter is mandatory: default to the first store when none selected
  useEffect(() => {
    if (stores.length > 0 && selectedStoreIds.length === 0) {
      setSelectedStoreIds([String(stores[0].id)]);
    }
  }, [stores, selectedStoreIds.length]);

  const updateSelectedStoreIds = (ids: string[]) => {
    if (ids.length === 0) {
      return;
    }
    setSelectedStoreIds(ids);
  };

  const storeFilterKey =
    selectedStoreIds.length > 0 ? selectedStoreIds.join(",") : "";

  const value = useMemo<ReportStoreContextValue>(
    () => ({
      stores,
      isLoadingStores: storesQuery.isLoading,
      selectedStoreIds,
      setSelectedStoreIds: updateSelectedStoreIds,
      storeFilterKey,
    }),
    [stores, storesQuery.isLoading, selectedStoreIds, storeFilterKey],
  );

  return (
    <ReportStoreContext.Provider value={value}>
      {children}
    </ReportStoreContext.Provider>
  );
};

export const useReportStore = (): ReportStoreContextValue => {
  const ctx = useContext(ReportStoreContext);
  if (!ctx) {
    throw new Error("useReportStore must be used within a ReportStoreProvider");
  }
  return ctx;
};
